/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


// Enums
export enum ParticipantType {
  USER = 'USER',
  MANAGER = 'MANAGER',
  ADMIN = 'ADMIN',
  EXECUTIVE = 'EXECUTIVE'
}

export enum ComponentType {
  ITEM = 'ITEM',
  COLLECTION = 'COLLECTION'
}

// Entity types
export interface Participant {
  id: number;
  uuid: string;
  username: string;
  firstName?: string;
  lastName?: string;
  type: ParticipantType;
  title?: string;
  contactNote?: string;
  manager?: Participant;
  managerId?: number;
  managerName?: string;
  company?: Company;
  companyId?: number;
  companyName?: string;
  userInactive?: boolean;
  changePassword?: boolean;
  demoParticipant?: boolean;
  createdDate?: string;
  email?: string;
}

export interface Company {
  id: number;
  name: string;
  email?: string;
  phoneNumber?: string;
  contactNotes?: string;
  bidFrontPage?: string;
  bidLastPage?: string;
  generateBidCost: number;
  updateBidCost: number;
  credits: number;
}

export interface Bid {
  id: number;
  name: string;
  filePath: string;
  price?: number;
  dueDate?: string;
  createdDate?: string;
  lastModified?: string;
  bidBodyCount?: number;
  internalNotes?: string;
  coverSheet?: string;
  archiveUntilDate?: string;
  hasGeneratedPdf?: boolean;
  margin?: number;
  submitted?: boolean;
  submittedDate?: string;
  accepted?: boolean;
  acceptedDate?: string;
  submissionNotes?: string;
  archivedFlag?: boolean;
  bidGroupId?: number;
  versionNumber?: number;
  pdfGenerateDate?: string;
  author?: {
    id: number;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  displayIndex?: boolean;
}

export interface Component {
  id: number;
  name: string;
  filePath?: string;
  uploadDate: string;
  expirationDate?: string;
  lastUsedInBidDate?: string;
  price?: number;
  manufacturerName: string;
  manufacturerCode: string;
  company?: Company;
  frontPageTemplate?: string;
  type?: ComponentType;
}

export interface ComponentCollectionItem {
  id: number;
  collection: Component;
  component: Component;
  suggestedCount: number;
  itemOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface BidBodyCollectionItem {
  id?: number;
  componentId: number;
  componentName?: string;
  manufacturerCode?: string;
  actualCount: number;
}

export interface BidBody {
  id: number;
  bid: Bid;
  component: Component;
  componentId: number;
  sequenceNumber: number;
  notes?: string;
  identificationCode?: string;
  discountPercentage?: number;
  discountFlat?: number;
  count: number;
  frontPage?: string;
  displayIndex?: boolean;
  componentType?: ComponentType | string;
  componentName?: string;
  collectionItems?: BidBodyCollectionItem[];
}

export interface BidResult {
  id?: number;
  bidId: number;
  bidAccepted?: boolean;
  bidSubmittedDate?: string;
  bidAcceptedDate?: string;
  competingBidCount?: number;
  knownCompetitors?: string;
  notes?: string;
}

// Request DTOs
export interface LoginRequest {
  username: string;
  password: string;
}

export interface CreateParticipantRequest {
  username: string;
  password?: string; // Optional because password will be set via email link
  firstName?: string;
  lastName?: string;
  email?: string;
  title?: string;
  contactNote?: string;
  type?: ParticipantType;
  managerId?: number;
  companyId?: number;
}

export interface UpdateParticipantRequest {
  type?: ParticipantType;
  firstName?: string;
  lastName?: string;
  title?: string;
  contactNote?: string;
  managerId?: number;
  companyId?: number;
  userInactive?: boolean;
}

export interface ChangePasswordRequest {
  newPassword: string;
}

export interface CreateBidRequest {
  name: string;
  authorId: number;
}

export interface UpdateBidRequest {
  name?: string;
  submissionNotes?: string;
}

export interface AddComponentToBidRequest {
  bidId: number;
  componentId: number;
  sequenceNumber: number;
}

export interface GenerateComponentRequest {
  bidId: number;
  outputDirectory: string;
}

export interface ReassignBidRequest {
  newAuthorId: number;
}

export interface UploadComponentRequest {
  file: File;
  manufacturerName?: string;
  manufacturerCode?: string;
  type?: ComponentType;
}

export interface UpdateComponentRequest {
  name: string;
  manufacturerName: string;
  manufacturerCode: string;
  price?: number;
  expirationDate?: string;
  filePath?: string;
  companyId?: number;
  frontPageTemplate?: string;
  type?: ComponentType;
}

// Response DTOs
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
}

export interface LoginResponse {
  success: boolean;
  token?: string;
  participant?: Participant;
  username?: string;
  type?: ParticipantType;
  message?: string;
  error?: string;
}

export interface ParticipantResponse {
  success: boolean;
  participant?: Participant;
  username?: string;
  type?: ParticipantType;
  error?: string;
}

export interface ParticipantListResponse {
  success: boolean;
  participants?: Participant[];
  count?: number;
  error?: string;
}

export interface BidResponse {
  success: boolean;
  bid?: Bid;
  bidId?: number;
  name?: string;
  filePath?: string;
  error?: string;
}

export interface BidListResponse {
  success: boolean;
  bids?: Bid[];
  count?: number;
  scope?: string;
  error?: string;
}

export interface ComponentResponse {
  success: boolean;
  component?: Component;
  componentId?: number;
  name?: string;
  filePath?: string;
  error?: string;
}

export interface ComponentListResponse {
  success: boolean;
  components?: Component[];
  count?: number;
  error?: string;
}

export interface CompanyListResponse {
  success: boolean;
  companies?: Company[];
  count?: number;
  error?: string;
}

export interface CompanyResponse {
  success: boolean;
  company?: Company;
  message?: string;
  error?: string;
}

export interface CompanyRequest {
  name?: string;
  email?: string;
  phoneNumber?: string;
  contactNotes?: string;
  bidFrontPage?: string;
  bidLastPage?: string;
  generateBidCost?: number;
  updateBidCost?: number;
  credits?: number;
}

export interface BidBodyResponse {
  success: boolean;
  bidBody?: BidBody;
  error?: string;
}

export interface BidBodyListResponse {
  success: boolean;
  bidBodies?: BidBody[];
  count?: number;
  error?: string;
}

export interface GenerateComponentResponse {
  success: boolean;
  filePath?: string;
  bidId?: number;
  error?: string;
}

export interface HealthResponse {
  status: string;
  service: string;
}

export interface MonthlyActivity {
  month: string; // e.g., "2024-01"
  monthName: string; // e.g., "January 2024"
  bidCount: number;
  componentCount: number;
}

export interface MonthlyActivityReport {
  monthlyActivities: MonthlyActivity[];
}

export interface MonthlyReportResponse {
  success: boolean;
  report?: MonthlyActivityReport;
  error?: string;
}

export interface BidDetail {
  id: number;
  name: string;
  price?: number;
  createdDate: string;
  authorName: string;
  authorId: number;
}

export interface ComponentDetail {
  id: number;
  name: string;
  manufacturerName: string;
  manufacturerCode: string;
  price?: number;
  uploadDate: string;
  companyName: string;
  companyId: number;
}

export interface MonthlyDetailResponse {
  success: boolean;
  monthName: string;
  yearMonth: string;
  bids: BidDetail[];
  components: ComponentDetail[];
  error?: string;
}

export interface BidCalendarItem {
  id: number;
  name: string;
  price?: number;
  dueDate: string;
  authorName: string;
  authorId: number;
  companyId: number;
  companyName: string;
  submitted?: boolean;
  accepted?: boolean;
}

export interface CompanyCalendarResponse {
  success: boolean;
  calendar: {
    year: number;
    month: number;
    companyId: number;
    companyName: string;
    bidsByDate: Record<string, BidCalendarItem[]>;
  };
  error?: string;
}

// Executive Report Types
export interface ExecutiveMonthlyActivity {
  year: number;
  month: number;
  monthName: string;
  totalBids: number;
  submittedBids: number;
  acceptedBids: number;
  notAcceptedBids: number;
}

export interface ExecutiveYearlyActivityResponse {
  success: boolean;
  year: number;
  companyId: number;
  companyName: string;
  monthlyData: ExecutiveMonthlyActivity[];
  error?: string;
}

export interface ExecutiveMonthlyBidSummaryResponse {
  success: boolean;
  year: number;
  month: number;
  monthName: string;
  category: string;
  companyId: number;
  companyName: string;
  bids: any[];
  count: number;
  totalUnmodifiedCost: number;
  totalSubmittedCost: number;
  error?: string;
}
